// REST client for the curation review endpoints. Plain fetch with
// the Rails CSRF token, following the newest backoffice pattern.

import { AiPayload, DirectOverrides, GateCheck, GateResult, SkillState } from './curationScoring';

export interface RejectionReasonOption {
  label: string;
  value: string;
}

export interface ServerScores {
  category: 'qualified' | 'probably_not_a_match' | null;
  computedContextScore: number | null;
  computedSkillsScore: number | null;
  computedYearsScore: number | null;
  contextScore: number | null;
  experienceScore: number | null;
  mustHaveAvg: number | null;
  niceToHaveAvg: number | null;
  overallScore: number | null;
  skillsScore: number | null;
  yearsScore: number | null;
}

export interface LocationComparison {
  candidate: {
    citizenships: string[];
    city: string | null;
    country: string | null;
    inEligibleLocation: boolean;
    timezone: string | null;
    utcOffset: number | null;
  };
  job: {
    citizenshipRequired: boolean;
    eligibleLocations: string[];
    eligibleTimezones: string[];
    excludedLocations: string[];
    officeAttendanceCount: number | null;
    officeAttendancePeriod: string | null;
    officeLocations: string[];
    openToWorldwide: boolean;
    visaSupport: boolean;
    workModel: string;
  };
}

export interface LanguageComparison {
  candidate: { code: string; label: string; level: number; name: string }[];
  job: { code: string; name: string }[];
}

export interface ReviewJob {
  companyName: string | null;
  experienceMax: number | null;
  experienceMin: number | null;
  officeLocations: string[];
  title: string;
  workModel: string;
}

export interface ServerReviewState {
  ai: {
    completedAt: string | null;
    error: string | null;
    model: string | null;
    payload: AiPayload | null;
    requestedAt: string | null;
  };
  comparison: { languages: LanguageComparison; location: LocationComparison };
  decision: 'approved' | 'rejected' | null;
  gate: { checks: GateCheck[]; decision: 'gate_passed' | 'gate_failed' };
  id: number;
  job: ReviewJob;
  overrides: { direct: Partial<DirectOverrides>; skills: Record<string, SkillState> };
  rejectionReasons: RejectionReasonOption[];
  scores: ServerScores;
  status: 'created' | 'scoring' | 'scored' | 'scoring_failed' | 'submitted';
  suggestedRejectionReason: string | null;
}

export interface DecisionPayload {
  decision: 'approve' | 'reject';
  force_general_list: boolean;
  handpicked: boolean;
  notes: string;
  rejection_candidate_message?: string;
  rejection_landing_message?: string;
  rejection_reason?: string;
  rejection_reason_other?: string;
  talent_city?: string;
  talent_country_code?: string;
}

export interface ReviewUrls {
  decision: string;
  review: string;
  score: string;
}

export const reviewUrls = (applicationId: number | string): ReviewUrls => ({
  decision: `/curators/applications/${applicationId}/curation_review/decision`,
  review: `/curators/applications/${applicationId}/curation_review`,
  score: `/curators/applications/${applicationId}/curation_review/score`,
});

function csrfToken(): string {
  const el = document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement | null;
  return el ? el.content : '';
}

async function request<T>(url: string, options: RequestInit = {}): Promise<T> {
  const response = await fetch(url, {
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'X-CSRF-Token': csrfToken(),
    },
    ...options,
  });

  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw Object.assign(new Error(`Request failed (${response.status})`), { body, status: response.status });
  }
  return body as T;
}

export const fetchReviewState = (urls: ReviewUrls): Promise<ServerReviewState> =>
  request<ServerReviewState>(urls.review);

export const triggerAiScore = (urls: ReviewUrls): Promise<{ status: string }> =>
  request<{ status: string }>(urls.score, { body: '{}', method: 'POST' });

export interface OverridesPatch {
  direct_overrides?: Partial<Record<'skills_pct' | 'years_pct' | 'context_pct', number | null>>;
  gate_overrides?: Record<string, GateResult | null>;
  skill_overrides?: Record<string, SkillState | null>;
}

export const saveOverrides = (urls: ReviewUrls, patch: OverridesPatch): Promise<ServerReviewState> =>
  request<ServerReviewState>(urls.review, { body: JSON.stringify(patch), method: 'PATCH' });

export const submitDecision = (
  urls: ReviewUrls,
  payload: DecisionPayload,
): Promise<{ status: string }> =>
  request<{ status: string }>(urls.decision, { body: JSON.stringify(payload), method: 'POST' });
